import { useWindowDimensions } from "react-native";

// Breakpoint constants - focused on md and below
const BREAKPOINTS = {
	xs: 480, // Extra small devices (phones)
	sm: 768, // Small devices (tablets)
	md: 1024, // Medium devices (small laptops) - main mobile/desktop cutoff
} as const;

export function useIsMobile() {
	const { height: windowHeight, width: windowWidth } = useWindowDimensions();
	const isMobileValue = windowWidth < BREAKPOINTS.md; // Original logic

	// Create the boolean result
	const result = Boolean(isMobileValue);

	// Add breakpoint properties - md and below only
	(result as any).xs = windowWidth < BREAKPOINTS.xs; // < 480px
	(result as any).sm = windowWidth >= BREAKPOINTS.xs && windowWidth < BREAKPOINTS.sm; // 480px - 768px
	(result as any).md = windowWidth >= BREAKPOINTS.sm && windowWidth < BREAKPOINTS.md; // 768px - 1024px

	// Convenience properties
	(result as any).mobile = windowWidth < BREAKPOINTS.sm; // < 768px (true mobile)
	(result as any).tablet = windowWidth >= BREAKPOINTS.sm && windowWidth < BREAKPOINTS.md; // 768px - 1024px
	(result as any).desktop = windowWidth >= BREAKPOINTS.md; // >= 1024px

	// Min-width checks
	(result as any).minXs = windowWidth >= BREAKPOINTS.xs;
	(result as any).minSm = windowWidth >= BREAKPOINTS.sm;
	(result as any).minMd = windowWidth >= BREAKPOINTS.md;

	// Max-width checks
	(result as any).maxXs = windowWidth < BREAKPOINTS.xs;
	(result as any).maxSm = windowWidth < BREAKPOINTS.sm;
	(result as any).maxMd = windowWidth < BREAKPOINTS.md;

	// Dimensions
	(result as any).width = windowWidth;
	(result as any).height = windowHeight;

	// Utility methods
	(result as any).is = (breakpoint: keyof typeof BREAKPOINTS) => {
		switch (breakpoint) {
			case "xs":
				return windowWidth < BREAKPOINTS.xs;
			case "sm":
				return windowWidth >= BREAKPOINTS.xs && windowWidth < BREAKPOINTS.sm;
			case "md":
				return windowWidth >= BREAKPOINTS.sm && windowWidth < BREAKPOINTS.md;
			default:
				return false;
		}
	};

	(result as any).atLeast = (breakpoint: keyof typeof BREAKPOINTS) => windowWidth >= BREAKPOINTS[breakpoint];
	(result as any).atMost = (breakpoint: keyof typeof BREAKPOINTS) => windowWidth < BREAKPOINTS[breakpoint];

	return result;
}

// Export breakpoints for use in other components
export { BREAKPOINTS };
